'use client';

import { useState, type MouseEvent } from 'react';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Tooltip } from '@/primitives';
import { StarIcon } from '@/primitives/icons';
import TraitLeaderInfoModal from './TraitLeaderInfoModal';

type TraitLeaderIconProps = {
  /** When false, renders nothing. */
  show?: boolean;
  fontSize?: number;
  /** Positioning / layout for the absolute badge wrapper (search grid / EBV table). */
  sx?: SxProps<Theme>;
  tooltip?: string;
  ariaLabel?: string;
  dataDdActionName?: string;
};

/**
 * Shared BREEDPLAN trait-leader star used on the animals search grid and the
 * animal-details EBV table. Click opens the approved explanation modal.
 */
export default function TraitLeaderIcon({
  show = true,
  fontSize = 16,
  sx,
  tooltip = 'Trait Leader',
  ariaLabel = 'Trait Leader. Opens explanation dialog.',
  dataDdActionName = 'Trait Leader Info',
}: TraitLeaderIconProps) {
  const [open, setOpen] = useState(false);

  if (!show) {
    return null;
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <Box
        component="span"
        sx={{
          lineHeight: 0,
          display: 'inline-flex',
          ...(sx as object),
        }}
      >
        <Tooltip title={tooltip} arrow placement="top">
          <IconButton
            size="small"
            aria-label={ariaLabel}
            data-dd-action-name={dataDdActionName}
            onClick={handleClick}
            sx={{
              p: 0,
              color: 'inherit',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <StarIcon sx={{ fontSize }} />
          </IconButton>
        </Tooltip>
      </Box>
      <TraitLeaderInfoModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
